import type {
  AssignModulesInput,
  AssignRolesInput,
  CreateStaffInput,
  DataResponse,
  ListStaffQuery,
  ModuleCode,
  PaginatedResponse,
  PermissionCode,
  RoleCode,
  UpdateStaffInput,
  User,
  UserProfile,
} from '@tracker/shared';
import { api } from '@/lib/api';
import { downloadXlsx, localFileStamp } from '@/lib/download';

export interface StaffListItem extends User {
  roles: RoleCode[];
  avatar_url: string | null;
  photo_url: string | null;
  new_photos_url: string | null;
}

export interface StaffDetail {
  user: User;
  profile: UserProfile | null;
  photo_url: string | null;
  new_photos_url: string | null;
  roles: RoleCode[];
  permissions: PermissionCode[];
  modules: ModuleCode[];
}

export const usersService = {
  async list(query: Partial<ListStaffQuery> = {}): Promise<PaginatedResponse<StaffListItem>> {
    const res = await api.get<PaginatedResponse<StaffListItem>>('/users', { params: query });
    return res.data;
  },

  /**
   * Downloads the filtered staff register as a styled .xlsx. Sends the same
   * params as list() — the backend ignores page/limit and returns every match,
   * including deactivated staff when the status filter allows them.
   */
  async exportXlsx(query: Partial<ListStaffQuery> = {}): Promise<void> {
    await downloadXlsx(
      api.get('/users/export/xlsx', { params: query, responseType: 'blob' }),
      `staff-${localFileStamp()}.xlsx`,
    );
  },

  async get(id: string): Promise<StaffDetail> {
    const res = await api.get<DataResponse<StaffDetail>>(`/users/${id}`);
    return res.data.data;
  },

  async create(input: CreateStaffInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/users', input);
    return res.data.data;
  },

  async update(id: string, input: UpdateStaffInput): Promise<void> {
    await api.patch(`/users/${id}`, input);
  },

  async deactivate(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  async assignRoles(id: string, input: AssignRolesInput): Promise<void> {
    await api.post(`/users/${id}/roles`, input);
  },

  async setModules(id: string, input: AssignModulesInput): Promise<void> {
    await api.post(`/users/${id}/modules`, input);
  },

  async removeModule(id: string, moduleCode: ModuleCode): Promise<void> {
    await api.delete(`/users/${id}/modules/${moduleCode}`);
  },

  async resetModulesToDefaults(id: string): Promise<void> {
    await api.post(`/users/${id}/modules/reset-to-defaults`);
  },

  async setPassword(id: string, new_password: string): Promise<void> {
    await api.post(`/users/${id}/password`, { new_password });
  },
};
